import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { tradingService } from '../../services/tradingService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Deployments (Strategies → Deployments): the durable "what did I deploy, when, and
// how has its capital changed / been withdrawn" ledger. Two blocks, both read-only:
//   • Live deployments — every currently-active deployment, grouped by basket (a
//     512-leg portfolio deploy is ONE row, not 512), across all accounts.
//   • Activity timeline — the append-only ExternalCashFlow ledger: deploy / withdraw
//     / allocation-change / capital top-up-or-draw, newest-first.
// Backend groups server-side (apps/trading/views.DeploymentActivityView) so the
// client never receives a multi-thousand-row leg dump.

const fmtUsd = (v, dp = 0) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: dp });
};
const fmtSignedUsd = (v) => {
  const n = Number(v || 0);
  return `${n >= 0 ? '+' : '−'}${fmtUsd(Math.abs(n))}`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

// Event kind → { label, chip colour }. Directed capital: in = emerald, out = red.
const KIND_META = {
  deploy_open:        { label: 'Deploy',        cls: 'bg-emerald-500/15 text-emerald-500' },
  // A strategy stop is capital moving INSIDE the account (sleeve → account cash), never money leaving it (2026-10-16).
  stop_close:         { label: 'Stopped',       cls: 'bg-gray-500/15 text-gray-400' },
  alloc_change:       { label: 'Alloc change',  cls: 'bg-amber-500/15 text-amber-500' },
  external_topup:     { label: 'Top-up',        cls: 'bg-emerald-500/15 text-emerald-500' },
  external_withdraw:  { label: 'Withdraw',      cls: 'bg-red-500/15 text-red-500' },
  rebalance_transfer: { label: 'Rebalance',     cls: 'bg-gray-500/15 text-gray-400' },
  settlement_payout:  { label: 'Settlement',    cls: 'bg-sky-500/15 text-sky-500' },
};
const kindMeta = (k) => KIND_META[k] || { label: k, cls: 'bg-gray-500/15 text-gray-400' };

function AccountBadge({ isModel, isPaper }) {
  const { label, cls } = isModel
    ? { label: 'EXT BOOK', cls: 'bg-violet-500/15 text-violet-400' }
    : isPaper
      ? { label: 'Paper', cls: 'bg-sky-500/15 text-sky-500' }
      : { label: 'LIVE', cls: 'bg-emerald-500/15 text-emerald-500' };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide ${cls}`}>{label}</span>;
}

function Section({ title, subtitle, right, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 dark:bg-[#0d1117]">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left min-w-0">
          <span className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
          <span className="min-w-0">
            <span className="font-serif text-base text-gray-900 dark:text-gray-100">{title}</span>
            {subtitle && <span className="ml-2 text-[11px] text-gray-500">{subtitle}</span>}
          </span>
        </button>
        {right}
      </div>
      {open && <div className="overflow-x-auto scrollbar-thin">{children}</div>}
    </div>
  );
}

function LiveDeployments({ rows, fmtName }) {
  const cols = useMemo(() => [
    { key: 'name', type: 'string', get: (r) => fmtName(r) },
    { key: 'account', type: 'string', get: (r) => r.account_name || '' },
    { key: 'legs', type: 'number', get: (r) => r.n_strategies },
    { key: 'alloc', type: 'number', get: (r) => r.allocation_usd },
    { key: 'since', type: 'string', get: (r) => r.activated_at || '' },
    { key: 'epoch', type: 'number', get: (r) => r.epoch ?? 0 },
  ], [fmtName]);
  const { sorted, toggle, indicator } = useSortableRows(rows, cols, { key: 'alloc', dir: 'desc' });

  if (!rows.length) return <div className="px-4 py-6 text-sm text-gray-500">No live deployments.</div>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200 dark:border-gray-800">
          <SortableTh label="Basket / Strategy" colKey="name" toggle={toggle} indicator={indicator} align="left" className="px-4 py-2" />
          <SortableTh label="Account" colKey="account" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
          <SortableTh label="Legs" colKey="legs" toggle={toggle} indicator={indicator} className="px-3 py-2" />
          <SortableTh label="Allocation" colKey="alloc" toggle={toggle} indicator={indicator} className="px-3 py-2" />
          <SortableTh label="Since" colKey="since" toggle={toggle} indicator={indicator} className="px-3 py-2" />
          <SortableTh label="Epoch" colKey="epoch" toggle={toggle} indicator={indicator} className="px-3 py-2" />
        </tr>
      </thead>
      <tbody>
        {sorted.map((r, i) => (
          <tr key={`${r.group}-${r.portfolio_id ?? r.strategy_id}-${r.account_id}-${i}`}
              className="border-b border-gray-100 dark:border-gray-900 hover:bg-gray-50 dark:hover:bg-gray-900/40">
            <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{fmtName(r)}</td>
            <td className="px-3 py-2 whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5">
                <AccountBadge isModel={r.is_model} isPaper={r.is_paper} />
                <span className="text-gray-500 text-xs">{r.account_name}</span>
              </span>
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{r.n_strategies}</td>
            <td className="px-3 py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-500">{fmtUsd(r.allocation_usd)}</td>
            <td className="px-3 py-2 text-right whitespace-nowrap text-gray-500 text-xs">{fmtDate(r.activated_at)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.epoch ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ClosingDeployments({ rows }) {
  const cols = useMemo(() => [
    { key: 'name', type: 'string', get: (r) => r.strategy_name || '' },
    { key: 'basket', type: 'string', get: (r) => r.portfolio_name || '' },
    { key: 'account', type: 'string', get: (r) => r.account_name || '' },
    { key: 'since', type: 'string', get: (r) => r.stop_requested_at || '' },
    { key: 'epoch', type: 'number', get: (r) => r.epoch ?? 0 },
  ], []);
  const { sorted, toggle, indicator } = useSortableRows(rows, cols, { key: 'since', dir: 'desc' });
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200 dark:border-gray-800">
          <SortableTh label="Strategy" colKey="name" toggle={toggle} indicator={indicator} align="left" className="px-4 py-2" />
          <SortableTh label="Basket" colKey="basket" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
          <SortableTh label="Account" colKey="account" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
          <SortableTh label="Stopped" colKey="since" toggle={toggle} indicator={indicator} className="px-3 py-2" />
          <SortableTh label="Epoch" colKey="epoch" toggle={toggle} indicator={indicator} className="px-3 py-2" />
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={`closing-${r.strategy_id}`} className="border-b border-gray-100 dark:border-gray-900 hover:bg-gray-50 dark:hover:bg-gray-900/40">
            <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
              {r.strategy_name}
              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-500/15 text-gray-500">Stopped — not yet sold</span>
            </td>
            <td className="px-3 py-2 text-gray-500 text-xs">{r.portfolio_name || '—'}</td>
            <td className="px-3 py-2 whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5">
                <AccountBadge isModel={r.is_model} isPaper={r.is_paper} />
                <span className="text-gray-500 text-xs">{r.account_name}</span>
              </span>
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap text-gray-500 text-xs">{fmtDate(r.stop_requested_at)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.epoch ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ActivityTimeline({ rows }) {
  const cols = useMemo(() => [
    { key: 'date', type: 'string', get: (r) => r.date || '' },
    { key: 'kind', type: 'string', get: (r) => kindMeta(r.kind).label },
    { key: 'label', type: 'string', get: (r) => r.label || '' },
    { key: 'account', type: 'string', get: (r) => r.account_name || '' },
    { key: 'legs', type: 'number', get: (r) => r.n_legs },
    { key: 'amount', type: 'number', get: (r) => r.amount },
  ], []);
  const { sorted, toggle, indicator } = useSortableRows(rows, cols, { key: 'date', dir: 'desc' });

  if (!rows.length) {
    return (
      <div className="px-4 py-6 text-sm text-gray-500">
        No capital-flow events recorded yet. Deploys, withdrawals and allocation changes appear here as they happen.
      </div>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200 dark:border-gray-800">
          <SortableTh label="Date" colKey="date" toggle={toggle} indicator={indicator} align="left" className="px-4 py-2" />
          <SortableTh label="Event" colKey="kind" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
          <SortableTh label="Basket / Strategy" colKey="label" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
          <SortableTh label="Account" colKey="account" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
          <SortableTh label="Legs" colKey="legs" toggle={toggle} indicator={indicator} className="px-3 py-2" />
          <SortableTh label="Capital" colKey="amount" toggle={toggle} indicator={indicator} className="px-4 py-2" />
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const m = kindMeta(r.kind);
          const neg = Number(r.amount) < 0;
          return (
            <tr key={r.key} className="border-b border-gray-100 dark:border-gray-900 hover:bg-gray-50 dark:hover:bg-gray-900/40">
              <td className="px-4 py-2 whitespace-nowrap text-gray-500 text-xs">{fmtDate(r.date)}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${m.cls}`}>{m.label}</span>
                {r.internal && <span className="ml-1 text-[10px] text-gray-400">internal</span>}
              </td>
              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{r.label || '—'}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {r.account_name
                  ? <span className="inline-flex items-center gap-1.5"><AccountBadge isModel={r.is_model} isPaper={r.is_paper} /><span className="text-gray-500 text-xs">{r.account_name}</span></span>
                  : <span className="text-gray-400 text-xs">—</span>}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{r.n_legs}</td>
              <td className={`px-4 py-2 text-right tabular-nums font-medium ${neg ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-500'}`}>
                {fmtSignedUsd(r.amount)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function DeploymentsPage() {
  const [data, setData] = useState({ current: [], closing: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [acct, setAcct] = useState('all');           // 'all' | account id (string)
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setErr('');
    try {
      const params = acct === 'all' ? {} : { account: acct };
      const { data: d } = await tradingService.getDeploymentActivity(params);
      setData({ current: d.current || [], closing: d.closing || [], events: d.events || [] });
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not load deployments.');
    } finally { setLoading(false); }
  }, [acct]);

  useEffect(() => { load(); }, [load]);
  // Light auto-poll so a new deploy / withdrawal surfaces without a manual refresh.
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const fmtName = useCallback(
    (r) => (r.group === 'strategy' ? r.strategy_name : r.portfolio_name) || '—', []);

  // Account options are derived from what actually appears in the feed (no extra call).
  const accountOptions = useMemo(() => {
    const seen = new Map();
    [...data.current, ...data.events].forEach((r) => {
      if (r.account_id != null && !seen.has(r.account_id)) {
        seen.set(r.account_id, r.account_name || `#${r.account_id}`);
      }
    });
    return Array.from(seen, ([id, name]) => ({ id: String(id), name }));
  }, [data]);

  const totals = useMemo(() => {
    const deployed = data.current.reduce((s, r) => s + Number(r.allocation_usd || 0), 0);
    const legs = data.current.reduce((s, r) => s + Number(r.n_strategies || 0), 0);
    const accts = new Set(data.current.map((r) => r.account_id)).size;
    return { deployed, legs, accts, baskets: data.current.length };
  }, [data.current]);

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl">Deployments</h1>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Every live deployment and the append-only capital timeline — deploys, withdrawals
            and allocation changes — across all accounts. Portfolio deploys are shown per basket,
            not per leg.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Account filter — options come from what's actually deployed. */}
          {accountOptions.length > 1 && (
            <select
              value={acct}
              onChange={(e) => setAcct(e.target.value)}
              className="bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-brand-gold"
            >
              <option value="all">All accounts</option>
              {accountOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <button
            onClick={() => load()}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-xs border border-gray-300 dark:border-gray-700 hover:border-brand-gold disabled:opacity-50"
            title="Refresh"
          >
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Deployed capital', value: fmtUsd(totals.deployed), cls: 'text-emerald-600 dark:text-emerald-500' },
          { label: 'Baskets live', value: totals.baskets },
          { label: 'Legs', value: totals.legs.toLocaleString() },
          { label: 'Accounts', value: totals.accts },
        ].map((t) => (
          <div key={t.label} className="border border-gray-200 dark:border-gray-800 rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{t.label}</div>
            <div className={`mt-1 text-lg font-medium tabular-nums ${t.cls || 'text-gray-900 dark:text-gray-100'}`}>{t.value}</div>
          </div>
        ))}
      </div>

      {err && <div className="text-sm text-red-500 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}

      {loading && !refreshedAt ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="space-y-4">
          <Section title="Live deployments" subtitle={`${totals.baskets} active`}>
            <LiveDeployments rows={data.current} fmtName={fmtName} />
          </Section>
          {data.closing.length > 0 && (
            <Section title="Stopped — not yet sold" subtitle={`${data.closing.length} sleeve${data.closing.length === 1 ? '' : 's'} awaiting a final exit`}>
              <ClosingDeployments rows={data.closing} />
            </Section>
          )}
          <Section title="Activity timeline" subtitle={`${data.events.length} event${data.events.length === 1 ? '' : 's'}`}>
            <ActivityTimeline rows={data.events} />
          </Section>
        </div>
      )}

      {refreshedAt && (
        <div className="text-[10px] text-gray-400 text-right">
          Updated {refreshedAt.toLocaleTimeString()} · auto-refreshes every 30s
        </div>
      )}
    </div>
  );
}
